import React, { useCallback, useMemo, useState } from 'react';
import { defineMessages, useIntl, type IntlShape } from 'react-intl';

import AvatarItem from '@atlaskit/avatar/avatar-item';
import { cssMap } from '@atlaskit/css';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { AgentAvatar } from '@atlaskit/rovo-agent-components/ui/AgentAvatar';
import AiAgentIcon from '@atlaskit/icon/core/ai-agent';
import IconTile from '@atlaskit/icon/icon-tile';
import { token } from '@atlaskit/tokens';

import type { CharlieTableRow } from '@atlassian/charlie-table/use-charlie-table';

import { type AgentIconType } from './agentIconType.ts';
import { RovoIcon } from './RovoIcon.tsx';

/**
 * Row-model mixin for tables that render AI-agent activity rows. Intersect it into a
 * table's own row data model. All fields are optional so existing (people) rows are
 * unaffected. Shared across the Page viewers table and the Space/Site users activity
 * tables.
 */
export type ExpandableAgentRowFields = {
	isAgent?: boolean; // render the hexagon AgentAvatar instead of a person avatar
	isChildRow?: boolean; // an indented person who used the agent
	agentId?: string; // stable id used for expand state + child lookup
	isExpandable?: boolean; // agent has child rows → show the expand chevron
	isExpanded?: boolean; // current expand state (injected by useExpandableAgentRows)
	agentSubtitle?: string; // e.g. "with Mathew Bertrand and 3 others"
	onToggleExpand?: () => void; // toggles expand state for this agentId
	agentIcon?: AgentIconType; // fixed fallback icon for unresolved agents
};

export const agentActivityI18n: {
	expandAgentRow: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	collapseAgentRow: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	agentSubtitle: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	expandAgentRow: {
		id: 'confluence-analytics.agent-activity.expand-agent-row',
		defaultMessage: 'Show the people who used this agent',
		description:
			'Accessible label for the button that expands an AI agent row in an analytics table to reveal the people who used the agent.',
	},
	collapseAgentRow: {
		id: 'confluence-analytics.agent-activity.collapse-agent-row',
		defaultMessage: 'Hide the people who used this agent',
		description:
			'Accessible label for the button that collapses an expanded AI agent row in an analytics table.',
	},
	agentSubtitle: {
		id: 'confluence-analytics.agent-activity.agent-subtitle',
		defaultMessage: 'with {name}{count, plural, =0 {} one { and # other} other { and # others}}',
		description:
			'Subtitle shown under an AI agent row in an analytics table, listing the people who used the agent. {count} is the number of additional people beyond {name}: 0 renders just "with Mathew Bertrand", 1 renders "with Mathew Bertrand and 1 other", 2+ renders "with Mathew Bertrand and 3 others".',
	},
});

type AgentAvatarSize = 'small' | 'medium';

const styles = cssMap({
	toggleSmall: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '24px',
		height: '24px',
		flexShrink: 0,
		boxSizing: 'border-box',
		overflow: 'hidden',
		marginInlineStart: token('space.025'),
		marginInlineEnd: token('space.025'),
		backgroundColor: token('color.background.neutral.subtle'),
	},
	toggleMedium: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '32px',
		height: '32px',
		flexShrink: 0,
		boxSizing: 'border-box',
		overflow: 'hidden',
		marginInlineEnd: token('space.025'),
		backgroundColor: token('color.background.neutral.subtle'),
	},
	childIndent: {
		paddingInlineStart: token('space.500'),
	},
	iconWrapperSmall: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '24px',
		height: '24px',
		flexShrink: 0,
		marginInlineStart: token('space.025'),
		marginInlineEnd: token('space.025'),
	},
	iconWrapperMedium: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '32px',
		height: '32px',
		flexShrink: 0,
		marginInlineEnd: token('space.025'),
	},
	avatarOffsetInlineStart: {
		transform: 'translateX(2px)',
	},
});

/**
 * Renders an agent row's leading icon: the fixed AiAgent/Rovo icon for agents that
 * couldn't be resolved to an account, otherwise the deterministic hexagon AgentAvatar.
 */
export const AgentRowAvatar = ({
	name,
	agentIcon,
	avatarSize = 'small',
	isOffsetInlineStart = false,
}: {
	name?: string;
	agentIcon?: AgentIconType;
	avatarSize?: AgentAvatarSize;
	isOffsetInlineStart?: boolean;
}): React.JSX.Element => {
	const avatar = (() => {
		if (agentIcon === 'ai-agent') {
			return (
				<Box xcss={avatarSize === 'medium' ? styles.iconWrapperMedium : styles.iconWrapperSmall}>
					<IconTile icon={AiAgentIcon} label="" appearance="gray" size="small" />
				</Box>
			);
		}
		if (agentIcon === 'rovo') {
			return (
				<Box xcss={avatarSize === 'medium' ? styles.iconWrapperMedium : styles.iconWrapperSmall}>
					<RovoIcon label="" size={avatarSize === 'medium' ? 32 : 24} />
				</Box>
			);
		}
		return (
			<Box xcss={avatarSize === 'medium' ? styles.iconWrapperMedium : styles.iconWrapperSmall}>
				<AgentAvatar size={avatarSize} name={name} label="" />
			</Box>
		);
	})();

	return isOffsetInlineStart ? (
		<Box testId="agent-avatar-offset" xcss={styles.avatarOffsetInlineStart}>
			{avatar}
		</Box>
	) : (
		avatar
	);
};

/**
 * The agent's hexagon avatar doubles as the expand affordance: on hover or keyboard
 * focus it swaps to a chevron, and clicking it toggles the nested child rows. Keeping
 * the toggle on the avatar itself (rather than a separate inline control) matches the
 * design.
 */
export const AgentAvatarExpandToggle = ({
	name,
	isExpanded,
	onToggle,
	expandLabel,
	collapseLabel,
	agentIcon,
	avatarSize = 'small',
	isOffsetInlineStart = false,
}: {
	name?: string;
	isExpanded: boolean;
	onToggle: () => void;
	expandLabel: string;
	collapseLabel: string;
	agentIcon?: AgentIconType;
	avatarSize?: AgentAvatarSize;
	isOffsetInlineStart?: boolean;
}): React.JSX.Element => {
	const [isActive, setIsActive] = useState(false);
	const showChevron = isActive || isExpanded;

	return (
		<Pressable
			xcss={avatarSize === 'medium' ? styles.toggleMedium : styles.toggleSmall}
			aria-label={isExpanded ? collapseLabel : expandLabel}
			aria-expanded={isExpanded}
			onClick={(event) => {
				event.stopPropagation();
				onToggle();
			}}
			onMouseEnter={() => setIsActive(true)}
			onMouseLeave={() => setIsActive(false)}
			onFocus={() => setIsActive(true)}
			onBlur={() => setIsActive(false)}
		>
			{showChevron ? (
				isExpanded ? (
					<ChevronDownIcon label="" />
				) : (
					<ChevronRightIcon label="" />
				)
			) : (
				<AgentRowAvatar
					name={name}
					agentIcon={agentIcon}
					avatarSize={avatarSize}
					isOffsetInlineStart={isOffsetInlineStart}
				/>
			)}
		</Pressable>
	);
};

/**
 * Renders the name-column cell for an AI-agent row: the hexagon AgentAvatar (which
 * doubles as an expand toggle when the agent has child rows) plus an optional subtitle.
 * The consuming table supplies `name`/`subtitle` from its own row model.
 */
export const AgentActivityNameCell = ({
	name,
	subtitle,
	isExpandable,
	isExpanded,
	onToggleExpand,
	agentIcon,
	avatarSize = 'small',
	isOffsetInlineStart = false,
}: {
	name?: string;
	subtitle?: string;
	isExpandable?: boolean;
	isExpanded?: boolean;
	onToggleExpand?: () => void;
	agentIcon?: AgentIconType;
	avatarSize?: AgentAvatarSize;
	isOffsetInlineStart?: boolean;
}): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const avatar = isExpandable ? (
		<AgentAvatarExpandToggle
			name={name}
			isExpanded={Boolean(isExpanded)}
			onToggle={onToggleExpand!}
			expandLabel={formatMessage(agentActivityI18n.expandAgentRow)}
			collapseLabel={formatMessage(agentActivityI18n.collapseAgentRow)}
			agentIcon={agentIcon}
			avatarSize={avatarSize}
			isOffsetInlineStart={isOffsetInlineStart}
		/>
	) : (
		<AgentRowAvatar
			name={name}
			agentIcon={agentIcon}
			avatarSize={avatarSize}
			isOffsetInlineStart={isOffsetInlineStart}
		/>
	);

	return <AvatarItem avatar={avatar} primaryText={name} secondaryText={subtitle} />;
};

/** Wraps an agent's nested child (person) cell with a left indent. */
export const AgentChildRowIndent = ({
	children,
}: {
	children: React.ReactNode;
}): React.JSX.Element => <Box xcss={styles.childIndent}>{children}</Box>;

/**
 * Owns expand/collapse state and flattens parent rows into the array handed to the
 * table: each expanded agent's children are spliced directly beneath it, so sorting
 * and pagination (done by the caller) only ever act on the parent rows.
 */
export const useExpandableAgentRows = <TableDataModel extends ExpandableAgentRowFields>(
	parentRows: CharlieTableRow<TableDataModel>[],
	childrenByAgentId: Record<string, CharlieTableRow<TableDataModel>[]>,
): CharlieTableRow<TableDataModel>[] => {
	const [expandedAgentIds, setExpandedAgentIds] = useState<Set<string>>(() => new Set());

	const toggleAgentExpansion = useCallback((agentId: string) => {
		setExpandedAgentIds((previous) => {
			const next = new Set(previous);
			if (next.has(agentId)) {
				next.delete(agentId);
			} else {
				next.add(agentId);
			}
			return next;
		});
	}, []);

	return useMemo(() => {
		const rows: CharlieTableRow<TableDataModel>[] = [];

		parentRows.forEach((row) => {
			const { isAgent, isExpandable, agentId } = row.data;
			if (isAgent && isExpandable && agentId) {
				const isExpanded = expandedAgentIds.has(agentId);
				rows.push({
					...row,
					data: {
						...row.data,
						isExpanded,
						onToggleExpand: () => toggleAgentExpansion(agentId),
					},
				});
				if (isExpanded) {
					rows.push(...(childrenByAgentId[agentId] ?? []));
				}
			} else {
				rows.push(row);
			}
		});

		return rows;
	}, [parentRows, childrenByAgentId, expandedAgentIds, toggleAgentExpansion]);
};

export const getAgentSubtitle = (
	formatMessage: IntlShape['formatMessage'],
	name: string,
	count: number,
): string =>
	formatMessage(agentActivityI18n.agentSubtitle, {
		name,
		count,
	});
